import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../components/ui/button";
import { MultiSelectDropdown, type MultiSelectOption } from "../../components/MultiSelectDropdown";
import type { LogReviewFilterDivision } from "../../types/logReview";
import { DateTimePicker, type DateRangeValue } from "../../components/ui/DateTimePicker";

export interface LogReviewTopFilters {
  division: string[];
  application: string[];
  category: string[];
  /** ISO `yyyy-MM-dd` bounds; `undefined` when not picked. */
  dateRange: { from?: string; to?: string };
}

export interface LogReviewTopFilterBarProps {
  /** Cascading Division → Application → Category hierarchy. */
  divisions: LogReviewFilterDivision[];
  onApply?: (filters: LogReviewTopFilters) => void;
  onClear?: () => void;
}

const toOptions = (values: string[]): MultiSelectOption[] =>
  values.map((value) => ({ value, label: value }));

const uniq = (values: string[]): string[] => Array.from(new Set(values));

/** Default date range — first day of the current month through today. */
const defaultRange = (): DateRangeValue => {
  const today = new Date();
  return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today };
};

const toIso = (date?: Date): string | undefined => {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Keep only selections still valid against the freshly-derived option list. */
const retainValid = (selection: string[], allowed: string[]): string[] => {
  if (selection.length === 0) return selection;
  const set = new Set(allowed);
  const next = selection.filter((v) => set.has(v));
  return next.length === selection.length ? selection : next;
};

/**
 * Top filter bar for the Log Review surfaces — a cascading
 * Division → Application → Category picker plus a date range, with Filter /
 * Clear actions. Application options narrow to the chosen division(s);
 * Category options narrow to the chosen application(s). Owns its draft
 * selection and emits the applied set via `onApply`.
 */
export const LogReviewTopFilterBar = ({
  divisions,
  onApply,
  onClear,
}: LogReviewTopFilterBarProps) => {
  const [division, setDivision] = useState<string[]>([]);
  const [application, setApplication] = useState<string[]>([]);
  const [category, setCategory] = useState<string[]>([]);
  const [range, setRange] = useState<DateRangeValue | undefined>(defaultRange);

  // Future dates are not selectable — cap the calendar at today.
  const today = useMemo(() => new Date(), []);

  // Blank `businessDivision` ("") values are unnamed buckets — drop them from
  // the option list so the Division dropdown never shows an empty row.
  const divisionOptions = useMemo(
    () => divisions.map((d) => d.businessDivision).filter(Boolean),
    [divisions],
  );

  // Default selection — once the cascade loads, pre-select EVERY division
  // along with all of their applications and categories, then emit an initial
  // apply so the dashboard opens scoped to everything.
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current || divisions.length === 0) return;
    didInit.current = true;
    // Blank businessDivision values are dropped from the Division selection
    // (placeholder shown, payload sends `[]` not `[""]`) while their
    // applications + categories are still included, mirroring divisionOptions.
    const divs = uniq(divisions.map((d) => d.businessDivision).filter(Boolean));
    const apps = uniq(divisions.flatMap((d) => d.applications.map((a) => a.appName)));
    const cats = uniq(
      divisions.flatMap((d) => d.applications.flatMap((a) => a.categories)),
    );
    setDivision(divs);
    setApplication(apps);
    setCategory(cats);
    const initRange = defaultRange();
    onApply?.({
      division: divs,
      application: apps,
      category: cats,
      dateRange: { from: toIso(initRange.from), to: toIso(initRange.to) },
    });
  }, [divisions, onApply]);

  // Applications under the selected divisions (all when none selected).
  const availableApps = useMemo(() => {
    const source = division.length
      ? divisions.filter((d) => division.includes(d.businessDivision))
      : divisions;
    return uniq(source.flatMap((d) => d.applications.map((a) => a.appName)));
  }, [divisions, division]);

  // Categories under the selected applications (all available when none).
  const availableCategories = useMemo(() => {
    const apps = divisions
      .flatMap((d) => d.applications)
      .filter(
        (a) =>
          availableApps.includes(a.appName) &&
          (application.length === 0 || application.includes(a.appName)),
      );
    return uniq(apps.flatMap((a) => a.categories));
  }, [divisions, availableApps, application]);

  // Prune child selections when the parent narrows them out of range.
  useEffect(() => {
    setApplication((prev) => retainValid(prev, availableApps));
  }, [availableApps]);
  useEffect(() => {
    setCategory((prev) => retainValid(prev, availableCategories));
  }, [availableCategories]);

  /** All application names under the given division selection (all when none). */
  const appsForDivisions = (divs: string[]): string[] => {
    const source = divs.length
      ? divisions.filter((d) => divs.includes(d.businessDivision))
      : divisions;
    return uniq(source.flatMap((d) => d.applications.map((a) => a.appName)));
  };

  /**
   * All categories under the given application selection, scoped to the
   * current divisions (all of that scope's categories when no app is chosen).
   */
  const catsForApps = (apps: string[], divScope: string[]): string[] => {
    const source = (
      divScope.length
        ? divisions.filter((d) => divScope.includes(d.businessDivision))
        : divisions
    ).flatMap((d) => d.applications);
    const filtered = source.filter((a) => apps.length === 0 || apps.includes(a.appName));
    return uniq(filtered.flatMap((a) => a.categories));
  };

  // Changing the division RESETS its children — the application and category
  // selections clear (placeholders shown). They auto-fill only on Filter.
  const handleDivisionChange = (next: string[]) => {
    setDivision(next);
    setApplication([]);
    setCategory([]);
  };

  // Changing the application RESETS the category selection.
  const handleApplicationChange = (next: string[]) => {
    setApplication(next);
    setCategory([]);
  };

  // On Filter, auto-select DOWNSTREAM levels only — an empty level fills
  // (visibly and in the payload) only when a level above it carries a
  // selection, and a level the user already picked is never overwritten.
  // Filtering by Category alone therefore never selects Applications in the
  // dropdown; the payload still resolves empty levels to the full scope so
  // the emitted filter stays complete.
  const handleFilter = () => {
    let apps = application;
    if (apps.length === 0 && division.length > 0) {
      apps = appsForDivisions(division);
      setApplication(apps);
    }
    let cats = category;
    if (cats.length === 0 && (apps.length > 0 || division.length > 0)) {
      cats = catsForApps(apps, division);
      setCategory(cats);
    }
    const payloadApps = apps.length ? apps : appsForDivisions(division);
    const payloadCats = cats.length ? cats : catsForApps(payloadApps, division);
    onApply?.({
      division,
      application: payloadApps,
      category: payloadCats,
      dateRange: { from: toIso(range?.from), to: toIso(range?.to) },
    });
  };

  const handleClear = () => {
    const cleared = defaultRange();
    setDivision([]);
    setApplication([]);
    setCategory([]);
    setRange(cleared);
    onClear?.();
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="log-review-top-filter-bar"
    >
      <MultiSelectDropdown
        selectAll
        placeholder="Division"
        options={toOptions(divisionOptions)}
        selected={division}
        onChange={handleDivisionChange}
        searchable
        selectedNoun="Divisions"
        className="min-w-[10rem] max-w-[16rem]"
      />
      <MultiSelectDropdown
        selectAll
        placeholder="Application"
        options={toOptions(availableApps)}
        selected={application}
        onChange={handleApplicationChange}
        selectedNoun="Applications"
        disabled={availableApps.length === 0}
        className="min-w-[10rem] max-w-[16rem]"
      />
      <MultiSelectDropdown
        selectAll
        placeholder="Category"
        options={toOptions(availableCategories)}
        selected={category}
        onChange={setCategory}
        searchable
        selectedNoun="Categories"
        disabled={availableCategories.length === 0}
        className="min-w-[10rem] max-w-[16rem]"
      />
      <DateTimePicker
        variant="range"
        value={range}
        onValueChange={setRange}
        displayFormat="dd/MM/yyyy"
        placeholder="Date Range"
        maxDate={today}
        triggerClassName="w-[18rem]"
        numberOfMonths={2}
        showOutsideDays={false}
        aria-label="Date range filter"
      />
      {/* Never disabled. Re-applying the same selection is harmless — it just
          refetches — and a greyed-out Filter reads as "something is wrong with
          my selection" when nothing is. Any state the button could be in is a
          state the user is allowed to submit. */}
      <Button onClick={handleFilter} aria-label="Apply filters">
        Filter
      </Button>
      <Button variant="secondary" onClick={handleClear} aria-label="Clear filters">
        Clear
      </Button>
    </div>
  );
};
